const CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS = {
  LOADING_STARTED: 'loading-started',
  LOADING_FINISHED: 'loading-finished',
};

class CustomLocationAutocomplete extends HTMLElement {
  constructor() {
    super();

    this.isResultsContainerVisible = false;
    this.isInitialized = false;
    this.isGoogleApiInitialized = false;
    this.googleAutocompleteService = null;
    this.googlePlacesService = null;
    this.geocoder = null;
    this.input = this.querySelector('input[type="text"]');
    this.field = this.querySelector('.field');
    this.resultsContainer = this.querySelector(`.custom-location-autocomplete-field__results`);
    this.focusedResultIndex = -1;
    this.onInputForSuggestionsFieldListener = null;
    this.onFocusForSuggestionsFieldListener = null;
    this.onBlurForSuggestionsFieldListener = null;
    this.onKeyDownForSuggestionsListener = null;
    this.config = {
      debounceTimeout: this.dataset?.configDebounceTimeout ? Number(this.dataset.configDebounceTimeout) : 200,
      minSearchPhraseLength: this.dataset?.configMinSearchPhraseLength ? Number(this.dataset.configMinSearchPhraseLength) : 3,
      showMyLocationOption: this.dataset?.configShowMyLocationOption !== undefined ? this.dataset.configShowMyLocationOption === `true` : true,
      style: {
        resultsContainerMarginTop: this.dataset?.configStyleResultsContainerMarginTop ? Number(this.dataset.configStyleResultsContainerMarginTop) : 0,
        resultsContainerMarginBottom: this.dataset?.configStyleResultsContainerMarginBottom ? Number(this.dataset.configStyleResultsContainerMarginBottom) : 0,
      },
    };

    this.#setStyling();
  }

  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;

    this.field.querySelector('button.clear').addEventListener('click', this.#onClearInputButtonClick.bind(this));

    this.onInputForSuggestionsFieldListener = debounce(this.#onInputForSuggestionsField.bind(this), this.config.debounceTimeout)
    this.input.addEventListener('input', this.onInputForSuggestionsFieldListener);

    this.onFocusForSuggestionsFieldListener = this.#onFocusForSuggestions.bind(this)
    this.input.addEventListener('focus', this.onFocusForSuggestionsFieldListener);

    this.onBlurForSuggestionsFieldListener = this.#onBlurForSuggestions.bind(this);
    this.input.addEventListener('blur', this.onBlurForSuggestionsFieldListener);

    this.onKeyDownForSuggestionsListener = this.#onKeyDownForSuggestions.bind(this)
    window.addEventListener('keydown', this.onKeyDownForSuggestionsListener);
  }

  disconnectedCallback() {
    this.input.removeEventListener('input', this.onInputForSuggestionsFieldListener);
    this.input.removeEventListener('focus', this.onFocusForSuggestionsFieldListener);
    this.input.removeEventListener('blur', this.onBlurForSuggestionsFieldListener);
    window.removeEventListener('keydown', this.onKeyDownForSuggestionsListener);
    this.isInitialized = false;
  }

  async initGoogle() {
    try {
      if (this.isGoogleApiInitialized) return;
      this.isGoogleApiInitialized = true;

      const {AutocompleteService, PlacesService} = await google.maps.importLibrary("places");
      const {Geocoder} = await google.maps.importLibrary("geocoding");

      this.googleAutocompleteService = new AutocompleteService();
      this.googlePlacesService = new PlacesService(document.createElement(`div`));
      this.geocoder = new Geocoder();
    } catch (error) {
      this.isGoogleApiInitialized = false;
      console.error(`Error while initializing Google API: ${error}`);
    }
  }

  #setStyling() {
    this.resultsContainer.style.marginTop = `${this.config.style.resultsContainerMarginTop}px`;
    this.resultsContainer.style.marginBottom = `${this.config.style.resultsContainerMarginBottom}px`;
  }

  #onInputForSuggestionsField(event) {
    if (!this.isGoogleApiInitialized || !this.isInitialized) return;

    const searchPhrase = event?.target?.value?.trim() || null;

    if (searchPhrase === null || searchPhrase.length === 0) {
      this.#toggleClearInputButtonVisibility(false);
      this.#showMyLocationOptionOnly();
      return;
    }

    if (searchPhrase.length < this.config.minSearchPhraseLength) {
      return;
    }

    this.#toggleClearInputButtonVisibility(true);
    this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_STARTED));

    this.googleAutocompleteService.getPlacePredictions(
      {
        input: searchPhrase,
        componentRestrictions: {
          country: 'US',
        },
      },
      (suggestions, status) => {
        if (status !== 'OK' || suggestions.length === 0) {
          this.#toggleResultsVisibility(false);
          this.#clearResults();
          this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, {detail: {success: false}}));
          return;
        }

        suggestions.forEach((suggestion, index) => {
          this.#addResult(this.#getResultRowWithData(suggestion, this.input, this.resultsContainer), index === 0);
        });

        this.#toggleResultsVisibility(true);
        this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, {detail: {success: true}}));
      }
    );
  }

  #onKeyDownForSuggestions(event) {
    if (!this.isResultsContainerVisible) return;

    if (event.key === `ArrowUp` || event.key === `ArrowDown`) {
      event.preventDefault();

      let prevFocusedResultIndex;

      switch (event.key) {
        case `ArrowUp`:
          this.focusedResultIndex = this.focusedResultIndex - 1 >= 0
            ? this.focusedResultIndex - 1
            : this.resultsContainer.children.length - 1;

          prevFocusedResultIndex = this.focusedResultIndex + 1 < this.resultsContainer.children.length
            ? this.focusedResultIndex + 1
            : 0;
          break;
        case `ArrowDown`:
          this.focusedResultIndex = this.focusedResultIndex + 1 < this.resultsContainer.children.length
            ? this.focusedResultIndex + 1
            : 0;

          prevFocusedResultIndex = this.focusedResultIndex - 1 >= 0
            ? this.focusedResultIndex - 1
            : this.resultsContainer.children.length - 1;
          break;
      }

      /** below is a hack to programmatically focus-visible on contenteditable element */
      this.resultsContainer.children[this.focusedResultIndex].contentEditable = true;
      this.resultsContainer.children[this.focusedResultIndex].focus();
      this.resultsContainer.children[this.focusedResultIndex].contentEditable = false;
    }

    if (event.key === `Enter` && this.focusedResultIndex >= 0) {
      event.preventDefault();
      this.resultsContainer.children[this.focusedResultIndex].click();
    }
  }

  #onFocusForSuggestions() {
    if (this.input.value.length === 0) {
      this.#showMyLocationOptionOnly();
    }
  }

  #onBlurForSuggestions() {
    setTimeout(() => {
      if (this.input.value.length === 0) {
        this.#toggleResultsVisibility(false);
      }
    }, 100);
  }

  #onClearInputButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.input.value = '';
    this.#clearResults();
    this.#toggleClearInputButtonVisibility(false);
    this.input.focus();
  }

  #showMyLocationOptionOnly() {
    this.#clearResults();
    if (this.config?.showMyLocationOption && navigator.geolocation) {
      this.#addResult(this.#getMyLocationResultItem(), false);
    }
    this.#toggleResultsVisibility(true);
  }

  #getResultRowWithData(data) {
    const item = document.createElement('a');
    item.classList.add('item');
    item.setAttribute('tabindex', '0');
    item.innerHTML = `<div class="item__icon"><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><use xlink:href="#svg-location-marker"></use></svg></div><div class="item__content"><strong>${data?.structured_formatting?.main_text}</strong><br />${data?.structured_formatting?.secondary_text || ''}</div>`;

    item.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_STARTED));

      this.#getPlaceDetails(data.place_id).then(place => {
        this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, {detail: {success: true}}));
        publish(PUB_SUB_EVENTS.customLocationComponentPlaceChanged, { source: this.id, place });
        this.input.value = place.formatted_address;
        this.#clearResults();
      }).catch(this.#handlePlaceDetailsError.bind(this));
    });

    item.addEventListener('keydown', (event) => {
      if (event.key === `Enter`) {
        event.preventDefault();
        event.target.click();
      }
    });

    return item;
  };

  #handlePlaceDetailsError(error) {
    console.error(`Error while getting place details: ${error}`);
    this.input.value = ``;
    this.#clearResults();
    this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, {detail: {success: false}}));
  }

  #getMyLocationResultItem() {
    const item = document.createElement('a');
    item.classList.add('item');
    item.classList.add('my-location-item');
    item.setAttribute('tabindex', '0');
    item.innerHTML = `<div class="item__icon"><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><use xlink:href="#svg-location-marker"></use></svg></div><div class="item__content"><strong>Get my current location</strong></div>`;

    item.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (navigator.geolocation) {
        this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_STARTED));

        navigator.geolocation.getCurrentPosition((position) => {
          const locationLatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          this.geocoder
            .geocode({ location: locationLatLng })
            .then((response) => {
              if (response.results[0]) {
                this.#getPlaceDetails(response.results[0].place_id).then(place => {
                  publish(PUB_SUB_EVENTS.customLocationComponentPlaceChanged, { source: this.id, place });
                  this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, {detail: {success: true}}));
                  this.input.value = place.formatted_address;
                  this.#toggleClearInputButtonVisibility(true);
                  this.#clearResults();
                }).catch(this.#handlePlaceDetailsError.bind(this));
              }
            })
            .catch((error) => {
              console.error(`Error while geocoding user location: ${error}`);
              this.input.value = ``;
              this.#clearResults();
              this.#toggleClearInputButtonVisibility(false);
              this.dispatchEvent(new CustomEvent(CUSTOM_LOCATION_AUTOCOMPLETE_COMPONENT_EVENTS.LOADING_FINISHED, {detail: {success: false}}));
            });
        });
      }
    });

    return item;
  }

  #clearResults() {
    this.resultsContainer.innerHTML = '';
    this.focusedResultIndex = -1;
    this.#toggleResultsVisibility(false);
  }

  #addResult(item, isFirst) {
    if (isFirst) {
      this.#clearResults();
      if (this.config?.showMyLocationOption && navigator.geolocation) {
        this.resultsContainer.append(this.#getMyLocationResultItem());
      }
    }

    this.resultsContainer.append(item);
  }

  #toggleResultsVisibility(isVisible) {
    this.isResultsContainerVisible = isVisible;
    isVisible
      ? this.resultsContainer.classList.remove('hidden')
      : this.resultsContainer.classList.add('hidden');
  }

  #getPlaceDetails(placeId) {
    return new Promise((resolve, reject) => {
      this.googlePlacesService.getDetails(
        {
          placeId,
          fields: [
            'geometry',
            'address_components',
            'formatted_address',
            'types',
          ]
        },
        (place, status) => {
          if (status !== 'OK') {
            reject(status);
          }

          resolve(place);
        }
      );
    });
  }

  #toggleClearInputButtonVisibility(isVisible) {
    this.field.querySelector('button.clear').style.opacity = isVisible ? '1' : '0';
  }
}
customElements.define('custom-location-autocomplete', CustomLocationAutocomplete);
